import { ThemeOptions } from '@mui/material/styles';
import { lightColors, brand, iconBadgeTones, buildShadows } from './tokens';
import { buildComponents } from './componentOverrides';
import { ITorqTheme } from './torqTypes';

// Menu lateral: sempre escuro, também no tema claro. Não deriva de
// `lightColors` (pensadas para superfícies claras). Exportado: o tema escuro
// usa o mesmo menu.
export const menuPetroleo = {
  bg: brand.menuBase,
  logoAreaBg: 'transparent',
  border: 'rgba(168, 192, 205, 0.14)',
  textPrimary: '#FFFFFF',
  textSecondary: '#D7E5ED',
  hover: 'rgba(66, 242, 247, 0.07)',
  scrollbarThumb: 'rgba(168, 192, 205, 0.26)',
  // Selecionado: fundo ciano translúcido, barra lateral, texto e ícone ciano.
  activeGradient: 'rgba(66, 242, 247, 0.12)',
  activeAccentBar: brand.primary,
  activeText: brand.primary,
  iconDefault: '#A8C0CD',
  iconActive: brand.primary,
  submenuBg: brand.menuSecondary,
  submenuHover: 'rgba(66, 242, 247, 0.07)',
  submenuSelectedBg: 'rgba(66, 242, 247, 0.14)',
  submenuTextPrimary: '#D7E5ED',
  submenuIconDefault: '#A8C0CD',
  submenuIconSelected: brand.primary,
};

const buildLightTorq = (sidebar: typeof menuPetroleo): ITorqTheme => ({
  surface: {
    page: lightColors.page,
    level2: lightColors.level2,
    card: lightColors.card,
    elevated: lightColors.elevated,
  },
  border: {
    default: lightColors.borderDefault,
    strong: lightColors.borderStrong,
    focus: lightColors.borderFocus,
  },
  text: {
    primary: lightColors.textPrimary,
    secondary: lightColors.textSecondary,
    muted: lightColors.textMuted,
    disabled: lightColors.textDisabled,
  },
  hover: lightColors.hover,
  selected: lightColors.selected,
  scrollbarThumb: lightColors.scrollbarThumb,
  scrollbarThumbHover: lightColors.scrollbarThumbHover,
  accentText: brand.primaryDark,
  cardShadow: lightColors.cardShadow,
  dialogShadow: lightColors.dialogShadow,
  iconBadge: iconBadgeTones,
  sidebar: { ...sidebar, divider: sidebar.border },
});

const torq = buildLightTorq(menuPetroleo);

// `dark` de cada cor = versão legível como texto sobre fundo claro; `main` é
// o valor exato da paleta. Texto sobre ciano/verde/amarelo é escuro (branco
// não tem contraste neles).
const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: { main: brand.primary, dark: brand.primaryDark, light: brand.primaryLight, contrastText: brand.menuBase },
  secondary: { main: brand.info, dark: brand.infoButton, light: '#7DB2FF', contrastText: '#FFFFFF' },
  error: { main: brand.error, dark: brand.errorButton, light: '#FF959C', contrastText: '#FFFFFF' },
  warning: { main: brand.warning, dark: brand.warningButton, light: '#FFD566', contrastText: brand.menuBase },
  success: { main: brand.success, dark: brand.successButton, light: '#8ADD78', contrastText: brand.menuBase },
  info: { main: brand.info, dark: brand.infoButton, light: '#7DB2FF', contrastText: '#FFFFFF' },

  background: {
    paper: lightColors.paper,
    default: lightColors.page,
  },

  text: {
    primary: lightColors.textPrimary,
    secondary: lightColors.textSecondary,
    disabled: lightColors.textDisabled,
  },

  divider: lightColors.divider,
};

const lightTypography: ThemeOptions['typography'] = {
  fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',

  // Letter-spacing levemente negativo nos títulos maiores — é o que dá
  // aquele acabamento "apertado"/editorial de dashboard premium (Linear,
  // Vercel etc.), em vez do espaçamento parado do padrão do navegador.
  h1: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '2.5rem', letterSpacing: '-0.02em' },
  h2: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '2rem', letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '1.875rem', letterSpacing: '-0.015em' },
  h4: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '1.5rem', letterSpacing: '-0.01em' },
  h5: { fontWeight: 650, color: lightColors.textPrimary, fontSize: '1.375rem' },
  h6: { fontWeight: 600, color: lightColors.textPrimary, fontSize: '1rem' },

  subtitle1: { color: lightColors.textSecondary },
  subtitle2: { color: lightColors.textMuted },

  body1: { color: lightColors.textPrimary, lineHeight: 1.6, fontSize: '0.875rem' },
  body2: { color: lightColors.textSecondary, lineHeight: 1.5, fontSize: '0.8125rem' },

  caption: { color: lightColors.textMuted, fontSize: '0.75rem' },
  overline: { color: lightColors.textMuted },

  button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' },
};

// `components` não depende de `torq.sidebar`, então o mesmo resultado serve
// para as duas variantes do tema claro.
const lightComponents = buildComponents('light', torq);
const lightShadows = buildShadows('light');

export const lightThemeOptions: ThemeOptions = {
  palette: lightPalette,
  typography: lightTypography,
  // 6: `borderRadius: 2` no sx das telas multiplica este valor (= 12px).
  shape: { borderRadius: 6 },
  torq,
  components: lightComponents,
  shadows: lightShadows,
};
